// Diretta (Live Match) API Service
import {API_CONFIG} from './config';
import {Match, Commentary, Lineup, Player, ChatMessage, GCSSignal} from '../types/diretta';

const API_BASE_URL = API_CONFIG.BASE_URL;
const GCS_PUBLIC_URL = 'https://storage.googleapis.com/diretta';

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(() => resolve(), ms));

/**
 * Fetch commentaries for a match with retry logic (up to 6 retries)
 */
export async function fetchCommentaries(matchId: string): Promise<Commentary[]> {
  const MAX_RETRIES = 6;
  
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(`${API_BASE_URL}/match/${matchId}/commentaries`);
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (attempt === MAX_RETRIES) {
        console.error('Error fetching commentaries (final attempt):', error);
        return [];
      }
      
      // Linear backoff: 1s, 2s, 3s, 4s, 5s, 6s
      const waitTime = 1000 * (attempt + 1);
      console.log(`Error fetching commentaries (attempt ${attempt + 1}/${MAX_RETRIES + 1}), retrying in ${waitTime}ms...`);
      await delay(waitTime);
    }
  }
  return [];
}

/**
 * Fetch match summary (goals, cards, substitutions grouped by half)
 */
export async function fetchSummary(matchId: string): Promise<Record<string, SummaryIncident[]>> {
  try {
    const response = await fetch(`${API_BASE_URL}/match/${matchId}/summary`);
    if (!response.ok) {
      return {};
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching summary:', error);
    return {};
  }
}

/**
 * Refresh summary (POST to truncate + refetch from API, then GET)
 */
export async function refreshSummary(matchId: string): Promise<Record<string, SummaryIncident[]>> {
  try {
    // First POST to trigger refresh on backend
    await fetch(`${API_BASE_URL}/match/${matchId}/refresh-summary`, {
      method: 'POST',
    });
    // Then GET the fresh data
    return await fetchSummary(matchId);
  } catch (error) {
    console.error('Error refreshing summary:', error);
    return await fetchSummary(matchId); // Fallback to just fetching
  }
}

export interface SummaryIncident {
  incident_time: string;
  incident_team: number; // 1 = home, 2 = away
  incident_participants: {
    participant_name: string;
    incident_type: string; // GOAL, YELLOW_CARD, RED_CARD, SUBSTITUTION_OUT, ASSISTANCE, PENALTY_KICK
  }[];
}

/**
 * Fetch match details from FlashLive API or calendario
 * Handles both FlashLive event IDs (alphanumeric like "MypSBJN0") and calendario IDs (numeric)
 */
export async function fetchMatchDetails(matchId: string): Promise<Match | null> {
  // If alphanumeric (FlashLive event ID), fetch from FlashLive API
  if (/[a-zA-Z]/.test(matchId)) {
    try {
      const response = await fetch(
        `https://${FLASHLIVE_API_HOST}/v1/events/data?event_id=${matchId}&locale=it_IT`,
        {
          headers: {
            'x-rapidapi-host': FLASHLIVE_API_HOST,
            'x-rapidapi-key': FLASHLIVE_API_KEY,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data?.DATA?.EVENT) {
          // Transform FlashLive response to our Match format
          return transformFlashLiveMatchDetails(data.DATA.EVENT, matchId);
        }
      }
    } catch (error) {
      console.error('Error fetching from FlashLive:', error);
    }
  }
  
  // Fallback to backend calendario API for numeric IDs
  try {
    const response = await fetch(`${API_BASE_URL}/api/match/${matchId}`);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching match details:', error);
    return null;
  }
}

/**
 * Transform FlashLive event data to our Match format
 */
function transformFlashLiveMatchDetails(data: any, matchId: string): Match {
  // Extract date and time from timestamp
  const matchDate = data.START_TIME ? new Date(data.START_TIME * 1000) : new Date();
  const dateStr = matchDate.toISOString().split('T')[0];
  const timeStr = matchDate.toTimeString().slice(0, 5);
  
  // Determine status
  let status: 'scheduled' | 'live' | 'finished' = 'scheduled';
  const stageType = data.STAGE_TYPE?.toLowerCase() || data.STAGE?.toLowerCase() || '';
  if (stageType.includes('finish') || stageType.includes('termin')) {
    status = 'finished';
  } else if (stageType.includes('live') || stageType.includes('tempo') || stageType.includes('half') || stageType.includes('progress')) {
    status = 'live';
  }
  
  return {
    match_id: matchId,
    home_team: {
      name: data.HOME_NAME || 'Home Team',
      short_name: data.SHORTNAME_HOME || data.HOME_NAME || 'Home',
      logo: data.HOME_IMAGES?.[0] || undefined,
    },
    away_team: {
      name: data.AWAY_NAME || 'Away Team', 
      short_name: data.SHORTNAME_AWAY || data.AWAY_NAME || 'Away',
      logo: data.AWAY_IMAGES?.[0] || undefined,
    },
    score_home: parseInt(data.HOME_SCORE_CURRENT, 10) || 0,
    score_away: parseInt(data.AWAY_SCORE_CURRENT, 10) || 0,
    status,
    competition: data.TOURNAMENT_NAME || '',
    matchday: data.ROUND || undefined,
    date: dateStr,
    time: timeStr,
    venue: data.VENUE || undefined,
    minute: data.GAME_TIME && data.GAME_TIME !== '-1' ? data.GAME_TIME + "'" : undefined,
  };
}

/**
 * Fetch match score
 */
export async function fetchMatchScore(calendarioId: number): Promise<{home: number; away: number} | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/match/${calendarioId}/score`);
    if (!response.ok) {
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching score:', error);
    return null;
  }
}

/**
 * Check GCS signal file for updates
 */
export async function checkGCSSignal(matchId: string): Promise<GCSSignal | null> {
  try {
    const url = `${GCS_PUBLIC_URL}/match-${matchId}.json?_=${Date.now()}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking GCS signal:', error);
    return null;
  }
}

/**
 * Fetch match statistics (grouped by category)
 */
export async function fetchStats(matchId: string): Promise<StatsData | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/match/${matchId}/statistics`);
    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    console.error('Error fetching stats:', error);
    return null;
  }
}

/**
 * Check GCS signal for stats updates
 */
export async function checkStatsGCSSignal(matchId: string): Promise<GCSSignal | null> {
  try {
    const url = `${GCS_PUBLIC_URL}/stats-${matchId}.json?_=${Date.now()}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking stats GCS signal:', error);
    return null;
  }
}

/**
 * Check GCS signal for summary updates
 */
export async function checkSummaryGCSSignal(matchId: string): Promise<GCSSignal | null> {
  try {
    const url = `${GCS_PUBLIC_URL}/summary-${matchId}.json?_=${Date.now()}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      return null;
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error checking summary GCS signal:', error);
    return null;
  }
}

// Stats data structure (grouped by category)
export type StatsData = Record<string, StatItem[]>;

export interface StatItem {
  incident_name: string;
  value_home: string;
  value_away: string;
}

// Player incidents map (player_id -> list of incident types)
export interface PlayerIncidents {
  [playerId: string]: {
    goals: number;
    assists: number;
    yellowCard: boolean;
    redCard: boolean;
    substitutedOut: boolean;
    substitutedIn: boolean;
  };
}

// FlashLive API configuration
const FLASHLIVE_API_HOST = 'flashlive-sports.p.rapidapi.com';
const FLASHLIVE_API_KEY = '1e9b76550emshc710802be81e3fcp1a0226jsn069e6c35a2bb';
const FLASHLIVE_IMAGE_BASE = 'https://www.flashscore.com/res/image/data/';

/**
 * Fetch player incidents (goals, assists, cards, subs) from FlashLive summary
 */
export async function fetchPlayerIncidents(matchId: string): Promise<PlayerIncidents> {
  try {
    const response = await fetch(
      `https://${FLASHLIVE_API_HOST}/v1/events/summary?event_id=${matchId}&locale=en_INT`,
      {
        headers: {
          'x-rapidapi-host': FLASHLIVE_API_HOST,
          'x-rapidapi-key': FLASHLIVE_API_KEY,
        },
      }
    );
    
    if (!response.ok) {
      return {};
    }
    
    const data = await response.json();
    const incidents: PlayerIncidents = {};
    
    // Process each stage (1st Half, 2nd Half, etc.)
    if (data?.DATA && Array.isArray(data.DATA)) {
      for (const stage of data.DATA) {
        if (stage.ITEMS && Array.isArray(stage.ITEMS)) {
          for (const item of stage.ITEMS) {
            if (item.INCIDENT_PARTICIPANTS && Array.isArray(item.INCIDENT_PARTICIPANTS)) {
              for (const participant of item.INCIDENT_PARTICIPANTS) {
                const playerId = participant.PARTICIPANT_ID;
                if (!playerId) continue;
                
                // Initialize player if not exists
                if (!incidents[playerId]) {
                  incidents[playerId] = {
                    goals: 0,
                    assists: 0,
                    yellowCard: false,
                    redCard: false,
                    substitutedOut: false,
                    substitutedIn: false,
                  };
                }
                
                const type = participant.INCIDENT_TYPE;
                switch (type) {
                  case 'GOAL':
                  case 'PENALTY_SCORED':
                    incidents[playerId].goals++;
                    break;
                  case 'ASSISTANCE':
                    incidents[playerId].assists++;
                    break;
                  case 'YELLOW_CARD':
                    incidents[playerId].yellowCard = true;
                    break;
                  case 'RED_CARD':
                  case 'YELLOW_RED_CARD':
                    incidents[playerId].redCard = true;
                    break;
                  case 'SUBSTITUTION_OUT':
                    incidents[playerId].substitutedOut = true;
                    break;
                  case 'SUBSTITUTION_IN':
                    incidents[playerId].substitutedIn = true;
                    break;
                }
              }
            }
          }
        }
      }
    }
    
    return incidents;
  } catch (error) {
    console.log('Error fetching player incidents:', error);
    return {};
  }
}

/**
 * Fetch match lineups - tries FlashLive first, falls back to GCS/API
 */
export async function fetchLineups(matchId: string): Promise<Lineup | null> {
  try {
    // Try FlashLive API first
    const response = await fetch(
      `https://${FLASHLIVE_API_HOST}/v1/events/lineups?event_id=${matchId}&locale=it_IT`,
      {
        headers: {
          'x-rapidapi-host': FLASHLIVE_API_HOST,
          'x-rapidapi-key': FLASHLIVE_API_KEY,
        },
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      const lineups = transformFlashLiveLineups(data);
      if (lineups) {
        return lineups;
      }
    }
  } catch (error) {
    console.log('FlashLive lineups API error, trying fallback:', error);
  }

  // Fallback to GCS
  try {
    const gcsUrl = `${GCS_PUBLIC_URL}/lineups-${matchId}.json?_=${Date.now()}`;
    const gcsResponse = await fetch(gcsUrl);
    if (gcsResponse.ok) {
      return await gcsResponse.json();
    }
  } catch (error) {
    console.log('GCS lineups fallback failed:', error);
  }

  // Fallback to backend API
  try {
    const apiResponse = await fetch(`${API_BASE_URL}/api/match/${matchId}/lineups`);
    if (apiResponse.ok) {
      return await apiResponse.json();
    }
  } catch (error) {
    console.log('API lineups fallback failed:', error);
  }

  return null;
}

/**
 * Transform FlashLive API response to our Lineup format
 */
function transformFlashLiveLineups(data: any): Lineup | null {
  if (!data?.DATA || !Array.isArray(data.DATA)) {
    return null;
  }

  const startingLineup = data.DATA.find((d: any) => 
    d.FORMATION_NAME?.toLowerCase().includes('formazioni') || 
    d.FORMATION_NAME?.toLowerCase().includes('starting') ||
    d.PLAYER_GROUP_TYPE === 1
  );
  
  const benchData = data.DATA.find((d: any) => 
    d.FORMATION_NAME?.toLowerCase().includes('panchina') || 
    d.FORMATION_NAME?.toLowerCase().includes('bench') ||
    d.PLAYER_GROUP_TYPE === 2
  );

  if (!startingLineup?.FORMATIONS || startingLineup.FORMATIONS.length < 2) {
    return null;
  }

  // FORMATION_LINE 1 = Home team, FORMATION_LINE 2 = Away team
  const homeFormation = startingLineup.FORMATIONS.find((f: any) => f.FORMATION_LINE === 1);
  const awayFormation = startingLineup.FORMATIONS.find((f: any) => f.FORMATION_LINE === 2);
  
  const homeBench = benchData?.FORMATIONS?.find((f: any) => f.FORMATION_LINE === 1);
  const awayBench = benchData?.FORMATIONS?.find((f: any) => f.FORMATION_LINE === 2);

  const transformPlayer = (member: any): Player => ({
    number: member.PLAYER_NUMBER || 0,
    name: member.PLAYER_FULL_NAME || member.SHORT_NAME || 'Unknown',
    short_name: member.SHORT_NAME,
    position: member.PLAYER_POSITION?.toString(),
    image: member.LPI ? `${FLASHLIVE_IMAGE_BASE}${member.LPI}` : undefined,
    rating: member.LPR ? parseFloat(member.LPR) : undefined,
    captain: member.PLAYER_FULL_NAME?.includes('(C)') || false,
    player_id: member.PLAYER_ID,
  });

  // Sort players by PLAYER_POSITION for correct field placement
  const sortByPosition = (members: any[]) => 
    [...(members || [])].sort((a, b) => (a.PLAYER_POSITION || 0) - (b.PLAYER_POSITION || 0));

  return {
    home: {
      formation: homeFormation?.FORMATION_DISPOSTION?.replace('1-', '') || '', // Remove "1-" prefix
      players: sortByPosition(homeFormation?.MEMBERS).map(transformPlayer),
      substitutes: (homeBench?.MEMBERS || []).map(transformPlayer),
      coach: undefined, // FlashLive doesn't provide coach in lineups
    },
    away: {
      formation: awayFormation?.FORMATION_DISPOSTION?.replace('1-', '') || '',
      players: sortByPosition(awayFormation?.MEMBERS).map(transformPlayer),
      substitutes: (awayBench?.MEMBERS || []).map(transformPlayer),
      coach: undefined,
    },
  };
}

/**
 * Fetch chat messages (paginated) + chat status
 */
export async function fetchChatMessages(matchId: string, page: number = 1): Promise<{messages: ChatMessage[], chatStatus: string, hasMore: boolean, currentPage: number}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/match/${matchId}/chat?page=${page}`);
    if (!response.ok) {
      return {messages: [], chatStatus: 'not_started', hasMore: false, currentPage: 1};
    }
    const data = await response.json();
    return {
      messages: data.data || [],
      chatStatus: data.chat_status || 'not_started',
      hasMore: data.has_more || false,
      currentPage: data.current_page || 1,
    };
  } catch (error) {
    console.error('Error fetching chat:', error);
    return {messages: [], chatStatus: 'not_started', hasMore: false, currentPage: 1};
  }
}

/**
 * Fetch new chat messages since last message ID (for polling)
 */
export async function fetchNewChatMessages(matchId: string, lastMessageId: number): Promise<{messages: ChatMessage[], chatStatus: string, deletedIds: number[]}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/match/${matchId}/chat?last_message_id=${lastMessageId}`);
    if (!response.ok) {
      return {messages: [], chatStatus: 'live', deletedIds: []};
    }
    const data = await response.json();
    return {
      messages: data.data || [],
      chatStatus: data.chat_status || 'live',
      deletedIds: data.deleted_ids || [],
    };
  } catch (error) {
    console.error('Error fetching new chat messages:', error);
    return {messages: [], chatStatus: 'live', deletedIds: []};
  }
}

/**
 * Send chat message (requires auth)
 * Returns {success, errorMessage?, chatStatus?, action?}
 */
export async function sendChatMessage(
  matchId: string,
  message: string,
  token: string
): Promise<{success: boolean; errorMessage?: string; chatStatus?: string; action?: string}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/match/${matchId}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({message}),
    });
    const data = await response.json();
    if (!response.ok) {
      return {
        success: false,
        errorMessage: data.message || 'Errore nell\'invio del messaggio',
        chatStatus: data.chat_status,
        action: data.action,
      };
    }
    return {success: true};
  } catch (error) {
    console.error('Error sending chat:', error);
    return {success: false, errorMessage: 'Errore di connessione'};
  }
}

/**
 * Toggle a reaction on a chat message
 */
export async function sendReaction(
  messageId: number,
  emoji: string,
  token: string
): Promise<{success: boolean; reactions?: Record<string, number[]>}> {
  try {
    const response = await fetch(`${API_BASE_URL}/chat/message/${messageId}/react`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({emoji}),
    });
    const data = await response.json();
    if (!response.ok) {
      return {success: false};
    }
    return {success: true, reactions: data.reactions};
  } catch (error) {
    console.error('Error sending reaction:', error);
    return {success: false};
  }
}

/**
 * Fetch upcoming/recent matches from calendario
 */
export async function fetchMatches(): Promise<Match[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/calendario`);
    if (!response.ok) {
      return [];
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching matches:', error);
    return [];
  }
}
